import React, { useState, useEffect, useContext, useRef, useCallback } from 'react';
import styled, { keyframes, css } from 'styled-components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faComments, faSpinner, faSortAmountDown, faSortAmountUp, faInfoCircle, faHandshake } from '@fortawesome/free-solid-svg-icons';
import { AuthContext } from '../../context/AuthContext';
import { CommentsAPI } from '../../api';
import CommentItem from './CommentItem';
import CommentForm from './CommentForm';
import { handleApiError } from '../../utils/errorUtils';
import { useLocation, useNavigate } from 'react-router-dom';

// Define animations
const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const slideIn = keyframes`
  from {
    opacity: 0;
    transform: translateX(-10px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
`;

const CommentsContainer = styled.div`
  max-width: 800px;
  width: 100%;
  margin: 0 auto;
  padding: 15px 0;
  animation: ${fadeIn} 0.5s ease-out;
  
  @media (max-width: 768px) {
    padding-bottom: 90px; /* Increase space for fixed comment form */
    padding-top: 20px; /* Add top padding for better spacing with back button */
  }
`;

// Add a new component for the comments header
const CommentsHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 15px;
`;

const Title = styled.div`
  font-size: 18px;
  font-weight: 500;
  color: var(--text-light);
  display: flex;
  align-items: center;
  
  svg {
    margin-right: 8px;
    color: var(--text-dim);
    font-size: 16px;
  }
`;

const Controls = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const SortButton = styled.button`
  display: flex;
  align-items: center;
  background-color: rgba(59, 130, 246, 0.08);
  border: none;
  padding: 6px 10px;
  border-radius: 20px;
  color: var(--text-dim);
  font-size: 12px;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    background-color: rgba(59, 130, 246, 0.15);
    color: var(--text-light);
    transform: translateY(-1px);
  }
  
  svg {
    margin-right: 5px;
    font-size: 11px;
    transition: transform 0.3s ease;
  }
  
  ${props => props.$active && `
    background-color: rgba(59, 130, 246, 0.2);
    color: var(--primary-light);
  `}
`;

const LoadMoreButton = styled.button`
  display: block;
  margin: 20px auto 0;
  background-color: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.05);
  padding: 8px 16px;
  border-radius: 20px;
  color: var(--text-light);
  font-size: 14px;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    background-color: rgba(255, 255, 255, 0.15);
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  }
  
  &:active {
    transform: translateY(-1px);
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
  }
`;

const NoComments = styled.div`
  text-align: center;
  padding: 40px 0;
  color: var(--text-dim);
  font-size: 14px;
  opacity: 0.7;
  
  svg {
    font-size: 24px;
    margin-bottom: 10px;
    opacity: 0.5;
  }
`;

const LoadingSpinner = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 40px 0;
  color: var(--text-dim);
  
  svg {
    margin-right: 10px;
    animation: spin 1s linear infinite;
  }
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const LoginPrompt = styled.div`
  background-color: rgba(26, 26, 26, 0.4);
  backdrop-filter: blur(5px);
  border-radius: 20px;
  padding: 15px;
  margin-bottom: 20px;
  text-align: center;
  border: 1px solid rgba(255, 255, 255, 0.05);
  
  p {
    margin-bottom: 10px;
    color: var(--text-dim);
    font-size: 14px;
  }
  
  button {
    background-color: rgba(59, 130, 246, 0.2);
    color: var(--primary-light);
    border: 1px solid rgba(59, 130, 246, 0.1);
    padding: 8px 16px;
    border-radius: 20px;
    font-size: 14px;
    cursor: pointer;
    transition: all 0.2s ease;
    
    &:hover {
      background-color: rgba(59, 130, 246, 0.3);
      transform: translateY(-1px);
    }
  }
`;

const ErrorMessage = styled.div`
  background-color: rgba(220, 38, 38, 0.1);
  color: rgba(252, 165, 165);
  padding: 10px 15px;
  border-radius: 20px;
  margin-bottom: 15px;
  display: flex;
  align-items: center;
  
  svg {
    margin-right: 8px;
  }
`;

// Update the SystemMessage component to remove the sort button
const SystemMessage = styled.div`
  background-color: rgba(59, 130, 246, 0.1);
  border: 1px solid rgba(59, 130, 246, 0.2);
  border-radius: 12px;
  padding: 15px;
  margin-bottom: 20px;
  position: relative;
  animation: ${fadeIn} 0.6s ease-out;
  transition: all 0.3s ease;
  
  &:hover {
    background-color: rgba(59, 130, 246, 0.15);
    border-color: rgba(59, 130, 246, 0.3);
    transform: translateY(-1px);
  }
  
  h4 {
    color: var(--primary-light);
    font-weight: 600;
    margin-bottom: 10px;
    display: flex;
    align-items: center;
    font-size: 14px;
    
    svg {
      margin-right: 8px;
    }
  }
  
  ul {
    margin: 0;
    padding-left: 20px;
    
    li {
      margin-bottom: 6px;
      font-size: 13px;
      line-height: 1.4;
      color: var(--text-light);
    }
  }
  
  @media (max-width: 768px) {
    margin-bottom: 15px;
    padding: 12px;
  }
`;

// Add a highlight animation for linked comments
const highlightAnimation = keyframes`
  0% { background-color: rgba(59, 130, 246, 0.3); }
  50% { background-color: rgba(59, 130, 246, 0.15); }
  100% { background-color: transparent; }
`;

// Add highlighted style to CommentItem wrapper
const HighlightedComment = styled.div`
  animation: ${props => props.$isHighlighted ? css`${highlightAnimation} 3s ease-out` : 'none'};
  border-radius: 8px;
  margin: -8px;
  padding: 8px;
`;

// Process comments to handle nested replies
const processNestedReplies = async (comments) => {
  try {
    // If we don't have any comments, return empty array
    if (!comments || !Array.isArray(comments) || comments.length === 0) {
      return [];
    }

    // Copy the comments to avoid mutating the original
    const processedComments = [...comments];

    // For each comment, if it has reply IDs but not actual reply objects,
    // fetch those replies
    for (let i = 0; i < processedComments.length; i++) {
      const comment = processedComments[i];
      
      // Skip if no replies or already populated
      if (!comment.replies || !Array.isArray(comment.replies) || comment.replies.length === 0) {
        continue;
      }
      
      // Check if replies are just IDs that need to be fetched
      if (typeof comment.replies[0] === 'string' || typeof comment.replies[0] === 'number') {
        try {
          // Fetch replies for this comment
          const response = await CommentsAPI.getReplies(comment._id);
          if (response.data && response.data.success) {
            // Replace reply IDs with actual reply objects
            if (response.data.data?.replies) {
              comment.replies = response.data.data.replies;
            } else if (response.data.data) {
              // Handle the case where the tree response has the replies in the data object
              comment.replies = response.data.data.replies || [];
            } else {
              comment.replies = [];
            }
          }
        } catch (err) {
          console.error(`Error fetching replies for comment ${comment._id}:`, err);
          // Leave as-is if there's an error
        }
      }
    }
    
    return processedComments;
  } catch (err) {
    console.error('Error processing nested replies:', err);
    // Return the original comments if there's an error
    return comments;
  }
};

const CommentList = ({ contentType, contentId }) => {
  const { currentUser, openLoginModal } = useContext(AuthContext);
  const [comments, setComments] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sortNewest, setSortNewest] = useState(true);
  const [hasMore, setHasMore] = useState(false);
  const [page, setPage] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [highlightedCommentId, setHighlightedCommentId] = useState(null);
  
  // Add refs to prevent duplicate API calls
  const isLoadingComments = useRef(false);
  const fetchTimeoutRef = useRef(null);
  const commentRefs = useRef({});
  
  // Get location and navigate for URL handling
  const location = useLocation();
  const navigate = useNavigate();
  
  const LIMIT = 20;
  
  // Inside the CommentList component, add a function to sort comments
  const sortComments = (commentsToSort) => {
    if (!commentsToSort || !Array.isArray(commentsToSort)) return [];
    
    return [...commentsToSort].sort((a, b) => {
      // Parse the UTC date strings and ensure proper timezone handling
      const dateA = a.createdAt ? new Date(a.createdAt).getTime() : 0;
      const dateB = b.createdAt ? new Date(b.createdAt).getTime() : 0;
      return sortNewest ? dateB - dateA : dateA - dateB;
    });
  };
  
  // Update the fetchComments function to use the correct API method
  const fetchComments = async (reset = false) => {
    try {
      // Don't set loading if we're just loading more comments for infinite scroll
      if (reset) {
        setIsLoading(true);
      }
      setError(null);
      
      // If reset, start from page 1
      if (reset) {
        setPage(1);
        setComments([]);
        setHasMore(true);
      }
      
      // Only fetch if there might be more comments
      if (!hasMore && !reset) {
        setIsLoading(false);
        return;
      }
      
      const currentPage = reset ? 1 : page;
      const skip = reset ? 0 : (page - 1) * LIMIT;
      
      // Use the correct API method: getContentComments instead of getComments
      const response = await CommentsAPI.getContentComments(contentType, contentId, {
        limit: LIMIT,
        skip: skip
      });
      
      if (response.data && response.data.success) {
        const newComments = response.data.data.comments || [];
        const total = response.data.data.total || 0;
        const processed = await processNestedReplies(newComments);
        
        // Combine with existing comments if not resetting
        const updatedComments = reset ? 
          processed : 
          [...comments, ...processed];
        
        // Sort the comments
        const sortedComments = sortComments(updatedComments);
        
        setComments(sortedComments);
        setHasMore((skip + newComments.length) < total);
        
        // Increment page for next fetch
        if (!reset) {
          setPage(prev => prev + 1);
        }
      }
    } catch (err) {
      console.error('Error fetching comments:', err);
      const errorInfo = handleApiError(err);
      setError(errorInfo.message || 'An error occurred while loading comments. Please try again.');
      
      // Auto-dismiss error after 5 seconds
      setTimeout(() => setError(null), 5000);
    } finally {
      setIsLoading(false);
    }
  };
  
  // Debounced fetch comments function
  const debouncedFetchComments = (reset = false) => {
    // Clear any existing timeout
    if (fetchTimeoutRef.current) {
      clearTimeout(fetchTimeoutRef.current);
    }
    
    // Set a new timeout
    fetchTimeoutRef.current = setTimeout(() => {
      fetchComments(reset);
      fetchTimeoutRef.current = null;
    }, 300); // 300ms debounce
  };
  
  useEffect(() => {
    debouncedFetchComments(true);
    
    // Cleanup function
    return () => {
      if (fetchTimeoutRef.current) {
        clearTimeout(fetchTimeoutRef.current);
      }
      isLoadingComments.current = false;
    };
  }, [contentId, contentType, sortNewest]);
  
  const handleCreateComment = async (content) => {
    if (!currentUser) {
      openLoginModal();
      return;
    }
    
    setIsSubmitting(true);
    try {
      const response = await CommentsAPI.createComment({
        content,
        content_id: contentId,
        content_type: contentType,
        parent_id: null
      });
      
      if (response.data && response.data.success) {
        // Add the new comment to the list based on sort order
        const newComment = response.data.data;
        
        // Add additional properties that might be needed for display
        const enhancedComment = {
          ...newComment,
          username: currentUser.username,
          avatar: currentUser.avatar,
          user_id: currentUser._id,
          replies: []
        };
        
        setComments(prev => {
          // Add the new comment to the beginning or end based on sort order
          const updatedComments = sortNewest 
            ? [enhancedComment, ...prev]
            : [...prev, enhancedComment];
            
          return updatedComments;
        });
      } else {
        // Handle error from the API
        setError(response.data?.error || 'Failed to post comment. Please try again.');
        // Show error for 3 seconds then clear it
        setTimeout(() => setError(null), 3000);
      }
    } catch (err) {
      console.error('Error creating comment:', err);
      setError('Failed to post comment. Please try again later.');
      // Show error for 3 seconds then clear it
      setTimeout(() => setError(null), 3000);
    } finally {
      setIsSubmitting(false);
    }
  };
  
  const handleReply = async (parentId, content) => {
    if (!currentUser) {
      openLoginModal();
      return;
    }
    
    try {
      const response = await CommentsAPI.createComment({
        content,
        content_id: contentId,
        content_type: contentType,
        parent_id: parentId
      });
      
      if (response.data && response.data.success) {
        const newReply = response.data.data;
        
        // Enhance the reply with user data
        const enhancedReply = {
          ...newReply,
          username: currentUser.username,
          avatar: currentUser.avatar,
          user_id: currentUser._id,
          replies: []
        };
        
        // Update the comments array with the new reply
        setComments(prevComments => {
          // Find the parent comment and add the reply
          return updateCommentWithReply(prevComments, parentId, enhancedReply);
        });
      } else {
        // Handle error from the API
        setError(response.data?.error || 'Failed to post reply. Please try again.');
        setTimeout(() => setError(null), 3000);
      }
    } catch (err) {
      console.error('Error creating reply:', err);
      setError('Failed to post reply. Please try again later.');
      setTimeout(() => setError(null), 3000);
    }
  };
  
  const updateCommentWithReply = (commentsArray, targetId, newReply) => {
    return commentsArray.map(comment => {
      if (comment._id === targetId) {
        // Add reply to this comment
        const updatedReplies = comment.replies ? 
          [...comment.replies, newReply] : [newReply];
        
        // Sort the replies based on the current sort order
        const sortedReplies = updatedReplies.sort((a, b) => {
          const dateA = a.createdAt ? new Date(a.createdAt).getTime() : 0;
          const dateB = b.createdAt ? new Date(b.createdAt).getTime() : 0;
          return sortNewest ? dateB - dateA : dateA - dateB;
        });
        
        return {
          ...comment,
          replies: sortedReplies
        };
      }
      
      // Check if the target comment is in the replies
      if (comment.replies && comment.replies.length > 0) {
        return {
          ...comment,
          replies: updateCommentWithReply(comment.replies, targetId, newReply)
        };
      }
      
      return comment;
    });
  };
  
  const handleEditComment = async (commentId, content) => {
    if (!currentUser) {
      openLoginModal();
      return;
    }
    
    try {
      // If content is an object with a content property, extract just the string
      const contentToSend = typeof content === 'object' && content.content ? 
        content.content : content;
      
      // Update the UI immediately for a better user experience
      const updateComment = (commentsArray, commentId, newContent) => {
        return commentsArray.map(comment => {
          if (comment._id === commentId) {
            // Make sure we're storing a string, not an object
            return { ...comment, content: newContent };
          }
          
          if (comment.replies && comment.replies.length > 0) {
            return {
              ...comment,
              replies: updateComment(comment.replies, commentId, newContent)
            };
          }
          
          return comment;
        });
      };
      
      setComments(prev => updateComment(prev, commentId, contentToSend));
        
      // Send the proper content format to the API - needs to be { content: string }
      const response = await CommentsAPI.updateComment(commentId, { content: contentToSend });
      
      if (!response.data || !response.data.success) {
        throw new Error(response.data?.error || 'Failed to update comment');
      }
    } catch (err) {
      console.error('Error updating comment:', err);
      setError('Failed to update comment. Please try again.');
      setTimeout(() => setError(null), 3000);
      throw new Error('Failed to update comment');
    }
  };
  
  const handleDeleteComment = async (commentId) => {
    if (!currentUser) return;
    
    try {
      // First update the UI for immediate feedback
      const removeComment = (commentsArray, commentId) => {
        // First, filter out the comment if it's at this level
        const filteredArray = commentsArray.filter(comment => comment._id !== commentId);
        
        // Then, recursively process any replies
        return filteredArray.map(comment => {
          if (comment.replies && comment.replies.length > 0) {
            // Create a new object with updated replies (immutable way)
            return {
              ...comment,
              replies: removeComment(comment.replies, commentId)
            };
          }
          return comment;
        });
      };
      
      // Update the UI immediately for a smooth experience
      setComments(prev => removeComment(prev, commentId));
      
      // Then send the delete request to the server
      const response = await CommentsAPI.deleteComment(commentId);
      
      // If the deletion failed on the server, we could restore the comment
      // but for simplicity, we'll just log the error
      if (!response.data || !response.data.success) {
        console.error('Error from API:', response.data?.error);
      }
    } catch (err) {
      console.error('Error deleting comment:', err);
      setError('Failed to delete comment. Please try again.');
      setTimeout(() => setError(null), 3000);
    }
  };
  
  // Update the toggleSort function to re-sort existing comments
  const toggleSort = () => {
    setSortNewest(!sortNewest);
    // Re-sort existing comments when sort order changes
    setComments(prev => sortComments(prev));
  };
  
  // Inside the CommentList component, before the return statement
  const systemComment = {
    _id: 'system-guidelines',
    username: 'System',
    avatar: 'default.jpeg',
    createdAt: new Date().toISOString(),
    content: `Be respectful and constructive in your comments.
No harassment, hate speech, or offensive content.
Keep comments relevant to the content.
No spam or self-promotion.`,
    user_id: 'system',
    replies: []
  };
  
  // Add this function for infinite scroll
  const handleScroll = useCallback(() => {
    if (isLoadingComments.current || !hasMore) return;
    
    // Check if we're near the bottom of the page
    const scrollTop = (document.documentElement && document.documentElement.scrollTop) || document.body.scrollTop;
    const scrollHeight = (document.documentElement && document.documentElement.scrollHeight) || document.body.scrollHeight;
    const clientHeight = document.documentElement.clientHeight || window.innerHeight;
    const scrolledToBottom = Math.ceil(scrollTop + clientHeight) >= scrollHeight - 300;
    
    if (scrolledToBottom) {
      isLoadingComments.current = true;
      debouncedFetchComments(false);
      setTimeout(() => {
        isLoadingComments.current = false;
      }, 1000);
    }
  }, [hasMore, debouncedFetchComments]);

  // Add scroll event listener
  useEffect(() => {
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [handleScroll]);
  
  // Function to extract comment ID from URL
  const getCommentIdFromUrl = useCallback(() => {
    const searchParams = new URLSearchParams(location.search);
    return searchParams.get('comment');
  }, [location.search]);
  
  // Function to scroll to a specific comment
  const scrollToComment = useCallback((commentId) => {
    if (!commentId) return;
    
    // Wait a bit to ensure the comment is rendered
    setTimeout(() => {
      const commentElement = commentRefs.current[commentId];
      if (commentElement) {
        commentElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
        setHighlightedCommentId(commentId);
        
        // Remove highlight after animation completes
        setTimeout(() => {
          setHighlightedCommentId(null);
        }, 3000);
      }
    }, 500);
  }, []);
  
  // Check URL for comment ID on mount and when URL changes
  useEffect(() => {
    const commentId = getCommentIdFromUrl();
    if (commentId) {
      // If we already have comments loaded, try to scroll to the comment
      if (comments.length > 0) {
        const commentExists = comments.some(c => c._id === commentId);
        if (commentExists) {
          scrollToComment(commentId);
        } else {
          // If comment not found in current page, fetch it specifically
          fetchCommentById(commentId);
        }
      } else {
        // Store the ID to scroll to after comments load
        setHighlightedCommentId(commentId);
      }
    }
  }, [location.search, comments.length, getCommentIdFromUrl, scrollToComment]);
  
  // Function to fetch a specific comment by ID
  const fetchCommentById = async (commentId) => {
    try {
      setIsLoading(true);
      const response = await CommentsAPI.getComment(commentId);
      
      if (response.data && response.data.success) {
        const fetchedComment = response.data.data;
        
        // Check if this comment belongs to the current content
        if (fetchedComment.content_type === contentType && 
            fetchedComment.content_id === contentId) {
          
          // Add the comment to the list if it's not already there
          setComments(prevComments => {
            const exists = prevComments.some(c => c._id === commentId);
            if (!exists) {
              return [fetchedComment, ...prevComments];
            }
            return prevComments;
          });
          
          // Scroll to the comment
          scrollToComment(commentId);
        } else {
          setError("The requested comment doesn't belong to this content.");
          setTimeout(() => setError(null), 5000);
        }
      } else {
        setError("Couldn't find the requested comment.");
        setTimeout(() => setError(null), 5000);
      }
    } catch (err) {
      console.error("Error fetching comment by ID:", err);
      setError("Failed to load the requested comment.");
      setTimeout(() => setError(null), 5000);
    } finally {
      setIsLoading(false);
    }
  };
  
  // Function to generate a shareable link for a comment
  const generateCommentLink = (commentId) => {
    const url = new URL(window.location.href);
    url.searchParams.set('comment', commentId);
    return url.toString();
  };
  
  // Function to copy comment link to clipboard
  const copyCommentLink = (commentId) => {
    const link = generateCommentLink(commentId);
    navigator.clipboard.writeText(link)
      .then(() => {
        // Show a temporary success message (you can implement this)
        console.log("Comment link copied to clipboard");
      })
      .catch(err => {
        console.error("Failed to copy comment link:", err);
      });
  };
  
  // After comments are loaded, check if we need to scroll to a highlighted comment
  useEffect(() => {
    if (!isLoading && highlightedCommentId) {
      scrollToComment(highlightedCommentId);
    }
  }, [isLoading, highlightedCommentId, scrollToComment]);
  
  return (
    <CommentsContainer>
      {/* Add a header with the sort button */}
      <CommentsHeader>
        <Title>
          <FontAwesomeIcon icon={faComments} />
          Comments
        </Title>
        <SortButton onClick={toggleSort} $active={sortNewest}>
          <FontAwesomeIcon icon={sortNewest ? faSortAmountDown : faSortAmountUp} />
          {sortNewest ? 'Newest' : 'Oldest'}
        </SortButton>
      </CommentsHeader>
      
      {/* Render the system comment with community guidelines */}
      <CommentItem 
        key={systemComment._id}
        comment={systemComment}
        onReply={() => {}}
        onEdit={() => {}}
        onDelete={() => {}}
        contentType={contentType}
        contentId={contentId}
        refreshComments={() => {}}
      />
      
      {error && (
        <ErrorMessage>
          <FontAwesomeIcon icon={faInfoCircle} />
          {error}
        </ErrorMessage>
      )}
      
      {currentUser ? (
        <CommentForm 
          onSubmit={handleCreateComment} 
          placeholder="Add a comment..." 
          buttonText="Post"
          hideGuidelines={true} // Hide the guidelines button since we have a system message
        />
      ) : (
        <LoginPrompt>
          <p>Sign in to leave a comment</p>
          <button onClick={openLoginModal}>Sign In</button>
        </LoginPrompt>
      )}
      
      {isLoading && comments.length === 0 ? (
        <LoadingSpinner>
          <FontAwesomeIcon icon={faSpinner} spin />
          Loading comments...
        </LoadingSpinner>
      ) : comments.length > 0 ? (
        <>
          {comments.map(comment => (
            <HighlightedComment 
              key={comment._id} 
              $isHighlighted={comment._id === highlightedCommentId}
              ref={el => commentRefs.current[comment._id] = el}
            >
              <CommentItem 
                comment={comment} 
                onReply={handleReply}
                onEdit={handleEditComment}
                onDelete={handleDeleteComment}
                contentType={contentType}
                contentId={contentId}
                refreshComments={() => debouncedFetchComments(true)}
                onCopyLink={() => copyCommentLink(comment._id)}
                isHighlighted={comment._id === highlightedCommentId}
              />
            </HighlightedComment>
          ))}
          
          {/* Replace LoadMoreButton with a loading indicator for infinite scroll */}
          {isLoading && hasMore && (
            <LoadingSpinner style={{ padding: '20px 0' }}>
              <FontAwesomeIcon icon={faSpinner} spin />
              Loading more comments...
            </LoadingSpinner>
          )}
        </>
      ) : (
        <NoComments>
          <div>
            <FontAwesomeIcon icon={faComments} />
          </div>
          <p>No comments yet. Be the first to comment!</p>
        </NoComments>
      )}
    </CommentsContainer>
  );
};

export default CommentList; 